/***
 *
 *   PORTAL IDENTITY HOOK
 *   Asks the server whether the support-link signature on this public page
 *   is valid. The client never verifies HMAC itself — it does not have the
 *   secret. A valid result is identity only, not a session.
 *
 **********/

import { useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useApiContext } from 'app/api';

export function usePortalIdentity(projectName) {
    const { callApi } = useApiContext();
    const [searchParams] = useSearchParams();

    const payload = {
        email: searchParams.get('e') || '',
        first_name: searchParams.get('fn') || '',
        last_name: searchParams.get('ln') || '',
        exp: searchParams.get('exp') || '',
        sig: searchParams.get('sig') || '',
    };

    const enabled = Boolean(projectName && payload.sig);

    const query = useQuery(
        ['portal-identify', projectName, payload.sig, payload.exp, payload.email],
        () =>
            callApi({
                method: 'POST',
                url: `/api/auth/project/${encodeURIComponent(projectName)}/portal/identify`,
                data: payload,
                auth: false,
            }),
        { enabled, staleTime: 60 * 1000 },
    );

    const data = query.data?.data;
    const valid = query.data?.status === 200 && data?.valid === true;

    const identity = useMemo(() => {
        if (!valid) return null;
        return {
            email: data.email,
            first_name: data.first_name,
            last_name: data.last_name,
        };
    }, [valid, data?.email, data?.first_name, data?.last_name]);

    const params = useMemo(
        () => (enabled ? { exp: payload.exp, sig: payload.sig } : null),
        [enabled, payload.exp, payload.sig],
    );

    return {
        isLoading: enabled && query.isLoading,
        identity,
        params,
    };
}
